<template>
  <div class="h-dvh flex flex-col bg-pitch-black text-total-white">
    <!-- Header -->
    <TopNavbar />
    <!-- Instructions Header -->
    <CentralHeader
      :title="'Instructions'"
      :count="isLoading ? null : totalInstructions"
      @search="filterInstructions"
      :routeName="'CreateInstructionsPage'"
    />

    <!-- Main Content -->
    <div class="flex-1 flex flex-col pb-14 overflow-y-auto">
      <div
        v-if="isLoading"
        class="text-center flex-1 flex justify-center items-center m-auto h-full"
      >
        Loading instructions...
      </div>
      <div
        v-else-if="errorMessage"
        class="text-center flex-1 flex justify-center items-center m-auto h-full text-red-500"
      >
        {{ errorMessage }}
      </div>
      <div
        v-else-if="
          Array.isArray(filteredInstructions) && filteredInstructions.length === 0
        "
        class="text-center flex-1 flex justify-center gap-3 items-center m-auto h-full text-total-white-500"
      >
        <div v-if="isSearchMode" class="text-center">No instructions found.</div>
        <button
          v-else
          @click="goToCreateInstruction"
          class="text-prime-golden flex items-center gap-3"
        >
          <i class="fa fa-plus text-2xl mr-2"></i>
          Click to add instructions
        </button>
      </div>
      <div v-else>
        <div v-for="(instruction, index) in filteredInstructions" :key="instruction.id">
          <InstructionCard :instruction="instruction" :index="index" />
        </div>
      </div>
    </div>

    <!-- Pagination Controls (Fixed at the bottom) -->
    <div
      class="flex justify-center items-center py-4 bg-pitch-black-900 fixed bottom-0 w-full border-t border-gray-600"
    >
      <button
        @click="previousPage"
        :disabled="currentPage == 1"
        class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-left"></i>
      </button>

      <span class="text-sm text-total-white mx-4">
        {{ currentPage }}/{{ totalPages }}
      </span>

      <button
        @click="nextPage"
        :disabled="currentPage >= totalPages"
        class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useInstructionStore } from "@/stores/instruction";
import TopNavbar from "@/components/TopNavbar.vue";
import CentralHeader from "@/components/CentralHeader.vue";
import InstructionCard from "@/components/InstructionCard.vue";
import { useRouter } from "vue-router";

const instructionStore = useInstructionStore();
const errorMessage = ref("");
const instructions = ref([]);
const filteredInstructions = ref([]);
const isLoading = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);
const isSearchMode = ref(false);
const totalInstructions = ref(0);

const router = useRouter();

onMounted(async () => {
  await fetchInstructions();
});

// Fetch instructions

const fetchInstructions = async () => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    await instructionStore.fetchInstructions(currentPage.value, instructionStore.size);
    instructions.value = instructionStore.instructions;
    totalInstructions.value = instructionStore.totalInstructions;
    totalPages.value = Math.max(
      Math.ceil(totalInstructions.value / instructionStore.size),
      1
    );
    filteredInstructions.value = instructions.value; // Initialize filtered instructions
  } catch (error) {
    errorMessage.value = "Failed to load instructions.";
    console.error("Error:", error);
  } finally {
    isLoading.value = false;
  }
};

// Filter instructions
const filterInstructions = async (query) => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    isSearchMode.value = !!query;
    currentPage.value = 1; // Reset to the first page on search
    if (query) {
      // Search instructions
      await instructionStore.searchInstructions(
        query,
        currentPage.value,
        instructionStore.size
      );
      instructions.value = instructionStore.instructions;
      totalInstructions.value = instructionStore.totalInstructions;
      totalPages.value = Math.max(
        Math.ceil(totalInstructions.value / instructionStore.size),
        1 // Ensure totalPages is at least 1
      );
      filteredInstructions.value = instructions.value;
    } else {
      // Fetch all instructions
      await fetchInstructions();
    }
  } catch (error) {
    errorMessage.value = "Failed to filter instructions.";
    console.error("Error filtering instructions:", error);
  } finally {
    isLoading.value = false;
  }
};

// Pagination controls
const previousPage = async () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
    await fetchInstructions();
  }
};

const nextPage = async () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
    await fetchInstructions();
  }
};

const goToCreateInstruction = () => {
  // Logic to navigate to create instruction page
  router.push({ name: "CreateInstructionsPage" });
};
</script>

<style scoped>
/* Add specific styles if necessary */
</style>
